import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  makeQueryState,
  normaliseListResponse,
  DEFAULT_PAGE_SIZE
} from './gridQuery';

// ─────────────────────────────────────────────────────────────────
// Glass-i — useErpMasterQuery
//
// Server-mode adapter hook for ErpDataGrid. Owns the `GridQueryState`
// (page / pageSize / search / sort / filters) and calls
// `adapter.list(query)` whenever it changes. Returns rows + total
// alongside imperative setters that the grid wires onto its toolbar
// + onChange handler.
//
// Search input is debounced (default 300 ms) so typing doesn't fire
// one request per keystroke. All other state changes — page, sort,
// filter — fire immediately.
//
// Mode boundary:
//   - useErpMaster (client mode) holds rows in local state and
//     filters/sorts in-memory.
//   - useErpMasterQuery (server mode) trusts the backend with all
//     pagination/sort/filter work and only renders the page it gets.
//
// Either hook produces a `gridHandlers` blob compatible with
// <ErpDataGrid {...gridHandlers} />.
// ─────────────────────────────────────────────────────────────────

/** @typedef {import('./gridTypes').GridApiAdapter}  GridApiAdapter  */
/** @typedef {import('./gridTypes').GridQueryState}  GridQueryState  */
/** @typedef {import('./gridTypes').GridDataResponse} GridDataResponse */

const DEFAULT_DEBOUNCE_MS = 300;

/**
 * @param {{
 *   adapter: GridApiAdapter,
 *   initialQuery?: Partial<GridQueryState>,
 *   pageSize?: number,
 *   debounceMs?: number,
 *   autoFetch?: boolean,
 *   onError?: (err:any)=>void
 * }} opts
 */
export default function useErpMasterQuery({
  adapter,
  initialQuery,
  pageSize = DEFAULT_PAGE_SIZE,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  autoFetch = true,
  onError
} = {}) {
  if (!adapter || typeof adapter.list !== 'function') {
    throw new Error(
      'useErpMasterQuery: adapter.list(query) is required for server mode'
    );
  }

  const [query, setQuery] = useState(() =>
    makeQueryState({ pageSize, ...(initialQuery || {}) })
  );
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(undefined);
  const [meta, setMeta] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reuse a single request token so an in-flight fetch is discarded
  // when a newer one starts (prevents out-of-order list responses
  // from clobbering the latest page).
  const requestIdRef = useRef(0);

  // Stash `onError` in a ref so a caller passing an inline arrow
  // (`onError: (e) => message.error(...)`) doesn't change the
  // `fetchOnce` reference on every render — which would otherwise
  // re-fire the auto-fetch useEffect endlessly. The latest function
  // is always read fresh through the ref at error time.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Debounce only the `search` string. Page/sort/filter changes fire
  // immediately so the UI feels responsive.
  const [debouncedSearch, setDebouncedSearch] = useState(query.search || '');
  useEffect(() => {
    if (debouncedSearch === query.search) return undefined;
    const t = setTimeout(() => setDebouncedSearch(query.search || ''), debounceMs);
    return () => clearTimeout(t);
  }, [query.search, debouncedSearch, debounceMs]);

  // Effective query passed to the adapter — substitutes the debounced
  // search value so a typing burst is one request, not N.
  const effectiveQuery = useMemo(
    () => ({ ...query, search: debouncedSearch }),
    [query, debouncedSearch]
  );

  const fetchOnce = useCallback(
    async (q) => {
      const id = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const raw = await adapter.list(q);
        if (requestIdRef.current !== id) return;
        const normalised = normaliseListResponse(raw, q);
        setRows(normalised.rows);
        setTotal(normalised.total);
        setSummary(normalised.summary);
        setMeta(normalised.meta);
      } catch (err) {
        if (requestIdRef.current !== id) return;
        setError(err);
        const handler = onErrorRef.current;
        if (typeof handler === 'function') handler(err);
      } finally {
        if (requestIdRef.current === id) setLoading(false);
      }
    },
    // Intentionally exclude `onError` — it's accessed via
    // `onErrorRef.current`. Including it would make `fetchOnce` a
    // new reference every render and the auto-fetch useEffect would
    // refire every render → infinite request loop.
    [adapter]
  );

  // Auto-fetch whenever the effective query changes.
  useEffect(() => {
    if (!autoFetch) return;
    fetchOnce(effectiveQuery);
  }, [autoFetch, effectiveQuery, fetchOnce]);

  // ── imperative setters ───────────────────────────────────────
  const setPage = useCallback((page) => {
    setQuery((q) => ({ ...q, page: Math.max(1, page | 0) }));
  }, []);
  const setPageSize = useCallback((size) => {
    setQuery((q) => ({ ...q, page: 1, pageSize: size | 0 }));
  }, []);
  const setSort = useCallback((sort) => {
    setQuery((q) => ({ ...q, page: 1, sort: Array.isArray(sort) ? sort : [] }));
  }, []);
  const setSearch = useCallback((search) => {
    setQuery((q) => ({ ...q, page: 1, search: search ?? '' }));
  }, []);
  const setFilters = useCallback((filters) => {
    setQuery((q) => ({
      ...q,
      page: 1,
      filters: Array.isArray(filters) ? filters : []
    }));
  }, []);
  const setAdvancedFilters = useCallback((advancedFilters) => {
    setQuery((q) => ({ ...q, page: 1, advancedFilters }));
  }, []);
  const patchQuery = useCallback((patch) => {
    setQuery((q) => ({ ...q, ...(patch || {}) }));
  }, []);
  const refetch = useCallback(() => fetchOnce(effectiveQuery), [fetchOnce, effectiveQuery]);

  // ── gridHandlers — drop directly onto <ErpDataGrid /> ────────
  //
  // The grid in server mode trusts these props verbatim. `dataSource`
  // is the current page only; `total` drives the pagination footer.
  const gridHandlers = useMemo(
    () => ({
      dataSource: rows,
      total,
      summary,
      meta,
      loading,
      error,
      serverMode: true,
      query,
      onRefresh: refetch,
      onQueryChange: patchQuery
    }),
    [rows, total, summary, meta, loading, error, query, refetch, patchQuery]
  );

  return {
    // state
    rows,
    total,
    summary,
    meta,
    loading,
    error,
    query,
    // setters
    setQuery,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    setFilters,
    setAdvancedFilters,
    patchQuery,
    refetch,
    // grid wiring
    gridHandlers
  };
}
